import { ApplicationSegment } from 'components/ApplicationSegment';
import { PeriodNameDropdown } from 'components/PeriodNameDropdown';
import { useResponsive } from 'hooks/useResponsive';
import { useSecurity } from 'modules/account/hooks/useSecurity';
import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import { defineMessages, useIntl } from 'react-intl';
import { Form, Menu } from 'semantic-ui-react';
import { PeriodName } from 'utils/periodDates';

const m = defineMessages({
  myEntries: {
    id: 'Dashboard.Tabs.MyEntries',
    defaultMessage: 'Personnel',
  },
  myTeamEntries: {
    id: 'Dashboard.Tabs.MyTeamEntries',
    defaultMessage: 'Mon équipe',
  },
  allEntries: {
    id: 'Dashboard.Tabs.AllEntries',
    defaultMessage: 'Toutes les entrées',
  },
  showDataForPeriod: {
    id: 'Dashboard.Period.ShowDataForPeriod',
    defaultMessage: 'afficher les données',
  },
  currentMonth: {
    id: 'Dashboard.Period.CurrentMonth',
    defaultMessage: 'du mois courant',
  },
  previousMonth: {
    id: 'Dashboard.Period.PreviousMonth',
    defaultMessage: 'du mois précédent',
  },
  currentYear: {
    id: 'Dashboard.Period.CurrentYear',
    defaultMessage: "de l'année en cours",
  },
  previousYear: {
    id: 'Dashboard.Period.PreviousYear',
    defaultMessage: "de l'année précédente",
  },
});

export enum DashboardTab {
  MyEntries,
  MyTeamEntries,
  AllEntries,
}

interface Props {
  currentTab: DashboardTab;
  onTabChange: (tab: DashboardTab) => void;
  periodName: PeriodName;
  onPeriodChange: (periodName: PeriodName) => void;
  periodDates: [Date, Date];
  setPeriodDates: (periodDates: [Date, Date]) => void;
}

export const Tabs: React.FC<Props> = ({
  currentTab,
  onTabChange,
  periodName,
  onPeriodChange,
  periodDates,
  setPeriodDates,
}) => {
  const { formatMessage } = useIntl();
  const { isAdmin } = useSecurity();
  const { isMobile } = useResponsive();

  const tabCount = isAdmin ? 3 : 2;

  const [customPeriodDates, setCustomPeriodDates] =
    useState<[Date, Date]>(periodDates);

  return (
    <>
      <div className={`${isMobile ? 'tw-mx-12' : undefined} tw-mb-8`}>
        <Menu
          color={isMobile ? undefined : 'green'}
          inverted={isMobile ? true : undefined}
          widths={isMobile ? tabCount : undefined}
          pointing
          secondary
          className="tw-border-t-0 tw-border-l-0 tw-border-r-0"
        >
          <Menu.Item
            active={currentTab === DashboardTab.MyEntries}
            onClick={() => onTabChange(DashboardTab.MyEntries)}
          >
            {formatMessage(m.myEntries)}
          </Menu.Item>
          <Menu.Item
            active={currentTab === DashboardTab.MyTeamEntries}
            onClick={() => onTabChange(DashboardTab.MyTeamEntries)}
          >
            {formatMessage(m.myTeamEntries)}
          </Menu.Item>

          {isAdmin && (
            <Menu.Item
              active={currentTab === DashboardTab.AllEntries}
              onClick={() => onTabChange(DashboardTab.AllEntries)}
            >
              {formatMessage(m.allEntries)}
            </Menu.Item>
          )}

          {!isMobile && (
            <Menu.Menu position="right">
              <Menu.Item>
                {formatMessage(m.showDataForPeriod)}
                <PeriodNameDropdown
                  periodName={periodName}
                  onChange={onPeriodChange}
                />
              </Menu.Item>
            </Menu.Menu>
          )}
        </Menu>

        {isMobile && (
          <div className="tw-flex tw-justify-end tw-text-white">
            {formatMessage(m.showDataForPeriod)}
            <PeriodNameDropdown
              periodName={periodName}
              onChange={onPeriodChange}
            />
          </div>
        )}
      </div>
      {periodName === 'custom' && (
        <ApplicationSegment>
          <Form>
            <Form.Field>
              <label className="tw-text-purple">
                {formatMessage({
                  id: 'Dashboard.CustomDates',
                  defaultMessage: 'Période personnalisée',
                })}
              </label>
              <DatePicker
                selectsRange={true}
                startDate={customPeriodDates[0]}
                endDate={customPeriodDates[1]}
                onChange={(newDates) => {
                  setCustomPeriodDates(newDates as [Date, Date]);

                  if (newDates[0] && newDates[1]) {
                    setPeriodDates(newDates as [Date, Date]);
                  }
                }}
                withPortal
                className="tw-w-80"
                fixedHeight
                showMonthDropdown
                showYearDropdown
                dropdownMode="select"
              />
            </Form.Field>
          </Form>
        </ApplicationSegment>
      )}
    </>
  );
};
